import React from 'react';
import styles from './Card.module.css';

/**
 * Card variant type.
 * - flat     : no shadow, --color-border outline
 * - elevated : --shadow-sm warm box-shadow
 * - focused  : 2px --color-primary border, no shadow
 */
type CardVariant = 'flat' | 'elevated' | 'focused';

/**
 * Props for Card.
 * Extends all standard HTML div attributes so callsites can pass
 * aria-*, data-testid, onClick, etc. without wrapper boilerplate.
 */
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Visual treatment for the card surface.
   * Defaults to "flat". An invalid or missing value falls back to "flat"
   * without throwing a runtime error (AC-004).
   */
  variant?: CardVariant;
  /** Card content. */
  children: React.ReactNode;
}

/** Lookup table: variant -> CSS module class name. */
const VARIANT_CLASS: Record<CardVariant, string> = {
  flat: styles.variantFlat ?? '',
  elevated: styles.variantElevated ?? '',
  focused: styles.variantFocused ?? '',
};

/**
 * Card -- responsive layout container with three visual treatments.
 *
 * AC-004: flat / elevated / focused variants use CSS tokens only.
 *         Invalid or missing variant silently falls back to flat.
 * AC-005: width 100%, padding --space-4, border-radius --radius-md,
 *         no self-applied margin.
 *
 * All styling uses CSS custom properties from src/styles/globals.css.
 * No hardcoded hex values or magic pixel numbers in source or CSS module.
 */
export const Card = React.memo<CardProps>(
  function Card({ variant, children, className, ...rest }: CardProps) {
    // Runtime-safe variant resolution: handles undefined (missing) and any
    // unexpected string that may arrive at runtime (AC-004 fallback to flat).
    const safeVariant: CardVariant =
      variant === 'elevated'
        ? 'elevated'
        : variant === 'focused'
          ? 'focused'
          : 'flat';

    const classList: string[] = [styles.card ?? '', VARIANT_CLASS[safeVariant]];
    if (className) classList.push(className);

    return (
      <div
        {...rest}
        className={classList.join(' ')}
        data-variant={safeVariant}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';
